import { computed } from '@vue/composition-api';
import { useStore } from '@nuxtjs/composition-api';
import cloneDeep from 'lodash/cloneDeep';

import { State } from '~/data/types/store';
import { getDiscipline, getHoursMinutesSecondsFromMinutes, getWorkoutDetails, getWorkoutStatus, getWorkoutType } from '~/data/training/get-training-plan-details';
import { useUser } from '~/data/account/use-user';
import { useTrainingSummary } from '~/data/stats/use-training-summary';
import { TrainingPlanItem, WorkoutStats } from '~/data/types';
import { COMPLETION_STATUS_MANUAL } from '~/constants';

type UpdateData = {
  notes: string | null,
  completionStatus: number | null,
  stats: WorkoutStats | null
};

export const useTrainingPlanItem = () => {
  const store = useStore<State>();
  const { isMetricSystem, timeZone } = useUser();
  const { getSummaries, updateWeekSummary } = useTrainingSummary();
  const isLoading = computed(() => store.state.trainingPlanItem.isLoading);
  const isSavingId = computed(() => store.state.trainingPlanItem.isSavingId);
  const trainingPlanItem = computed(() => store.state.trainingPlanItem.data);
  const isEvent = computed(() => trainingPlanItem.value ? getIsEvent(trainingPlanItem.value) : false);
  const isEventRecoveryWithNoMovement = computed(() => trainingPlanItem.value ? getIsEventRecoveryWithNoMovement(trainingPlanItem.value) : false);

  const getInitialStats = (
    trainingPlanItem: TrainingPlanItem | null,
    completionStatus: number | null = null,
  ): WorkoutStats | null => {
    const stats = trainingPlanItem?.imported_activity?.stats ?? trainingPlanItem?.stats ?? null;

    if (!stats || trainingPlanItem?.isCompleted) {
      return stats;
    }

    const localStats = cloneDeep(stats);
    let estimatedDuration = '00:00:00';

    switch (completionStatus) {
      case COMPLETION_STATUS_MANUAL.AVERAGE:
        estimatedDuration = getHoursMinutesSecondsFromMinutes(trainingPlanItem?.workout.duration_min ?? 0);
        break;
      case COMPLETION_STATUS_MANUAL.TARGET:
        estimatedDuration = getHoursMinutesSecondsFromMinutes(trainingPlanItem?.workout.duration_max ?? 0);
        break;
      default:
        break;
    }

    if (localStats.duration_moving) {
      localStats.duration_moving.value = estimatedDuration;
    } else if (localStats.duration_total) {
      localStats.duration_total.value = estimatedDuration;
    }

    return localStats;
  };

  const status = computed(() => trainingPlanItem.value
    ? getWorkoutStatus(trainingPlanItem.value, timeZone.value)
    : null,
  );

  const workoutType = computed(() => trainingPlanItem.value
    ? getWorkoutType(trainingPlanItem.value)
    : '',
  );

  const workoutDetails = computed(() => {
    if (!trainingPlanItem.value) {
      return null;
    }

    return getWorkoutDetails(trainingPlanItem.value, isMetricSystem.value);
  });

  const workoutDiscipline = computed(() => trainingPlanItem.value
    ? getDiscipline(trainingPlanItem.value)
    : '',
  );

  const clearData = () => {
    store.dispatch('trainingPlanItem/CLEAR_DATA');
  };

  const getIsEvent = (item: TrainingPlanItem): boolean => item.workout.categoryKey === 'event';

  const getIsEventRecoveryWithNoMovement = (item: TrainingPlanItem): boolean => item.workout.categoryKey === 'event_recovery'
    && (item.workout.distance_min || 0) === 0
    && (item.workout.duration_min || 0) === 0;

  const getTrainingPlanItem = async(id: number) => {
    await store.dispatch('trainingPlanItem/GET_TRAINING_PLAN_ITEM', id);
  };

  const updateTrainingPlanItem = async(item: TrainingPlanItem, data: UpdateData, forceShare: boolean = false): Promise<boolean> => {
    store.commit('trainingPlanItem/SET_IS_LOADING', true);
    store.commit('trainingPlanItem/SET_IS_SAVING_ID', item.id);

    try {
      const oldStatus = trainingPlanItem.value
        ? `${trainingPlanItem.value.completion_status_manual}-${trainingPlanItem.value.completion_status_automatic}`
        : null;
      const oldDistance = trainingPlanItem.value
        ? trainingPlanItem.value.stats?.distance?.value || trainingPlanItem.value.imported_activity?.stats?.distance?.value
        : null;
      const oldDurationMoving = trainingPlanItem.value
        ? trainingPlanItem.value.stats?.duration_moving?.value || trainingPlanItem.value.imported_activity?.stats?.duration_moving?.value
        : null;

      const payload: { [key: string]: any } = {
        id: item.id,
        data: {
          completed_note: data.notes,
        },
      };

      if (forceShare) {
        payload.data.autoShare = true;
      }

      if (data.completionStatus) {
        payload.data.completion_status_manual = data.completionStatus;
      }

      if (data.stats) {
        payload.data.stats = {};

        Object.keys(data.stats).forEach((key) => { // @ts-ignore key type
          payload.data.stats[key] = data.stats[key] && data.stats[key].length ? data.stats[key] : null;
        });
      }

      const newTrainingPlan = await store.dispatch('trainingPlanItem/UPDATE_TRAINING_PLAN_ITEM', payload);

      store.commit('trainingPlan/SET_TRAINING_PLAN_ITEM', newTrainingPlan);
      store.commit('dashboard/SET_TRAINING_PLAN_ITEM', newTrainingPlan);

      const newStatus = newTrainingPlan
        ? `${newTrainingPlan.completion_status_manual}-${newTrainingPlan.completion_status_automatic}`
        : null;

      let shouldRefreshSummaryData = oldStatus !== newStatus;

      if (!shouldRefreshSummaryData) {
        if (data.stats?.distance !== undefined) {
          const newDistance = newTrainingPlan
            ? newTrainingPlan.stats?.distance?.value || newTrainingPlan.imported_activity?.stats?.distance?.value
            : null;

          shouldRefreshSummaryData = oldDistance !== newDistance;
        }
      }

      if (!shouldRefreshSummaryData) {
        if (data.stats?.duration_moving !== undefined) {
          const newDurationMoving = newTrainingPlan
            ? newTrainingPlan.stats?.duration_moving?.value || newTrainingPlan.imported_activity?.stats?.duration_moving?.value
            : null;

          shouldRefreshSummaryData = oldDurationMoving !== newDurationMoving;
        }
      }

      if (shouldRefreshSummaryData) {
        store.commit('trainingPlan/CLEAR_CALENDAR');
        updateWeekSummary(item.planned_date);
        getSummaries();
      }

      store.commit('trainingPlanItem/SET_IS_SAVING_ID', null);
      return true;
    } catch {
      store.commit('trainingPlanItem/SET_IS_SAVING_ID', null);
      return false;
    }
  };

  return {
    clearData,
    getInitialStats,
    getIsEvent,
    getIsEventRecoveryWithNoMovement,
    getTrainingPlanItem,
    isEvent,
    isEventRecoveryWithNoMovement,
    isLoading,
    isSavingId,
    status,
    trainingPlanItem,
    updateTrainingPlanItem,
    workoutDetails,
    workoutDiscipline,
    workoutType,
  };
};
